import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { Navigate, matchPath, useLocation } from "react-router";
import { ErrorSplashScreen } from "./modules/generic/components/splash-screen";
import NotFound from "./pages/not-found";
import { usePermissions } from "./server/auth/use-permissions";

import type { WithAuthenticationRequiredOptions } from "@auth0/auth0-react";
import type { ComponentType, FC } from "react";

/** Where a reviewer is sent when they land anywhere that is not theirs. */
const reviewerHome = "/reviewer";

/**
 * The only paths a reviewer is allowed on. Matched against the pathname rather than declared
 * per-route, because `/moments/:id/review` and the `/moments/:id` form it sits beside share a
 * single `ProtectedRoute`, so a prop cannot tell them apart.
 */
const reviewerPaths = [
  `${reviewerHome}/*`,
  "/moments/:id/review",
  "/moments/:id/newsletter/review",
];

const NO_OPTIONS: WithAuthenticationRequiredOptions = {};

type Props<P> = {
  Component: ComponentType<P>;
  type?: "super_admin" | "ce_admin";
  options?: WithAuthenticationRequiredOptions;
} & P;

export const ProtectedRoute = <P extends object>({
  Component,
  type,
  options = NO_OPTIONS,
  ...props
}: Props<P>): React.JSX.Element => {
  const { error } = useAuth0();
  if (error) return <ErrorSplashScreen />;
  const AuthenticatedComponent = withAuthenticationRequired<P>(
    withPermissions(Component, type),
    options,
  );
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion, react/static-components -- React Compiler cannot prove this is static; refactoring it is a separate change
  return <AuthenticatedComponent {...(props as P)} />;
};

const withPermissions = <P extends object>(
  Component: ComponentType<P>,
  type?: "super_admin" | "ce_admin",
): FC<P> => {
  const WithPermissions = (props: P): React.JSX.Element => {
    const perms = usePermissions();
    const { pathname } = useLocation();

    // Wait even on untyped routes: until claims load, `reviewer` reads false, so rendering here
    // would flash the admin view before sending a reviewer to the correct page.
    if (perms.isLoading) {
      return <></>;
    }

    // A reviewer has no permissions beyond their own reviews, so everywhere else is a bounce home
    // rather than the 404 an admin would get.
    if (perms.reviewer && !reviewerPaths.some((path) => matchPath(path, pathname))) {
      return <Navigate replace to={reviewerHome} />;
    }

    if (type && !perms?.[type]) {
      return <NotFound />;
    }

    return <Component {...props} />;
  };
  return WithPermissions;
};
